import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import {
  CHIP_MENU_ACTIVE_TREATMENT_PILL,
  CHIP_MENU_ALIGN_CENTER,
  CHIP_MENU_ALIGN_LEFT,
  CHIP_MENU_ALIGN_RIGHT,
  CHIP_MENU_ITEM_TONE_DEFAULT,
  CHIP_MENU_ITEM_VARIANT_ITEM,
  CHIP_MENU_PLACEMENT_BOTTOM,
  CHIP_MENU_PLACEMENT_TOP,
  type ChipMenuActiveTreatment,
  type ChipMenuAlign,
  type ChipMenuPlacement,
  type ChipMenuItemTone,
  type ChipMenuItemVariant,
} from './ChipMenu.constants';
import {
  Header,
  HeaderCap,
  Item as StyledItem,
  ItemMeta,
  List,
  Panel,
  Separator,
} from './ChipMenu.styled';

// Lets `ChipMenu.Item` pick up the active treatment configured on the
// nearest `ChipMenu` root without callers having to thread the prop
// through every item. Keeps the items' API identical to before for
// existing consumers.
const ChipMenuActiveTreatmentContext = createContext<ChipMenuActiveTreatment>(
  CHIP_MENU_ACTIVE_TREATMENT_PILL
);

export interface ChipMenuProps {
  /** Called when an outside-click or Escape key would dismiss the menu. */
  onClose: () => void;
  align?: ChipMenuAlign;
  /** Which side of the anchor the panel opens on. Defaults to `bottom`. */
  placement?: ChipMenuPlacement;
  /**
   * Fixed panel width. Pass a number for pixels (e.g. `248`) or a CSS
   * length string (`'248px'`, `'14rem'`). When omitted the panel grows
   * to fit its content (subject to the built-in `min-width: 220px`).
   * Use this whenever the design pins the dropdown to a specific size,
   * e.g. the Process editor's crumb dropdown.
   */
  width?: number | string;
  /**
   * How the active row is highlighted (`pill` = tinted background +
   * primary text, `subtle` = just `onSurface` text on transparent bg).
   * Set this on the root and every Item inside the menu inherits it.
   * Defaults to `pill` (current Material chip-menu behaviour). Use
   * `subtle` for dropdowns where the row colour is the sole indicator
   * of active state — e.g. the Process editor's crumb dropdown.
   */
  activeTreatment?: ChipMenuActiveTreatment;
  /** Custom anchor positioning class for tweaks (e.g. negative offsets). */
  className?: string;
  /**
   * Render the panel via a React portal (default target: `document.body`)
   * using `position: fixed`. Use this when the anchor lives inside a
   * `transform`/`overflow` ancestor that traps the absolutely-positioned
   * panel in its own stacking context — e.g. a React Flow node, whose
   * per-node `transform` means a `z-index` bump can never paint the menu
   * above a neighbouring node. Requires {@link anchorRef} so the panel can
   * be positioned off the trigger's viewport rect; without it the panel
   * still portals but can't place itself.
   */
  portal?: boolean;
  /**
   * The element the portaled panel anchors to (typically the menu trigger).
   * Only read when {@link portal} is true. Position is computed from its
   * bounding rect on open and tracks scroll + resize while open.
   */
  anchorRef?: { readonly current: HTMLElement | null };
  /**
   * Explicit portal target. Defaults to `document.body` when {@link portal}
   * is true. Ignored otherwise.
   */
  portalContainer?: Element | null;
  children?: ReactNode;
  /**
   * Accessible name for the menu (rendered as `aria-label` on `role="menu"`).
   * Defaults to "Menu" so screen reader users always announce something.
   */
  ariaLabel?: string;
  dataTestId?: string;
}

export interface ChipMenuItemProps {
  children: ReactNode;
  icon?: ReactNode;
  /** Optional metadata appended to the right (e.g. "Limit"). */
  meta?: ReactNode;
  tone?: ChipMenuItemTone;
  /**
   * Visual variant. `item` (default) is a flat row; `button` is a filled
   * pill that styles itself based on `tone` (accent → primaryFixed bg,
   * danger → errorContainer bg, etc.). Use `button` for a primary action
   * inside the menu — e.g. "+ New process" or "+ New folder".
   */
  variant?: ChipMenuItemVariant;
  /** Pin the active treatment (e.g. for the currently-selected sort). */
  isActive?: boolean;
  disabled?: boolean;
  title?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  dataTestId?: string;
}

export interface ChipMenuHeaderProps {
  children: ReactNode;
  /** Capacity counter shown right-aligned in the header (e.g. "3 / 10"). */
  cap?: ReactNode;
  dataTestId?: string;
}

const PORTAL_GAP_PX = 6;

interface PortalCoords {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

// Project the anchor's viewport rect onto the panel's anchor point. Right
// align pins the panel's right edge to the anchor's right (via `right`) so
// we never need the panel's own width; centre keeps its translateX(-50%).
const computePortalCoords = (
  rect: DOMRect,
  align: ChipMenuAlign,
  placement: ChipMenuPlacement,
): PortalCoords => {
  const vertical =
    placement === CHIP_MENU_PLACEMENT_TOP
      ? { bottom: window.innerHeight - rect.top + PORTAL_GAP_PX }
      : { top: rect.bottom + PORTAL_GAP_PX };
  if (align === CHIP_MENU_ALIGN_RIGHT) {
    return { ...vertical, right: window.innerWidth - rect.right };
  }
  if (align === CHIP_MENU_ALIGN_CENTER) {
    return { ...vertical, left: rect.left + rect.width / 2 };
  }
  return { ...vertical, left: rect.left };
};

// `width` is passed inline rather than via a transient prop so a styled(ChipMenu)
// wrapper in the consumer doesn't have to know about a `$width` knob — the
// inline style wins the cascade unconditionally.
const widthStyleOf = (width: number | string | undefined): React.CSSProperties | undefined => {
  if (width === undefined) return undefined;
  return { width: typeof width === 'number' ? `${width}px` : width };
};

const positionStyleOf = (coords: PortalCoords): React.CSSProperties =>
  Object.fromEntries(Object.entries(coords).filter(([, value]) => value !== undefined));

// A shadow root retargets document events to its host, so the event's path,
// not its target, says whether the press landed inside the panel.
const isInside = (panel: HTMLElement, event: Event): boolean =>
  event.composedPath().includes(panel) || panel.contains(event.target as Node);

const ChipMenuRoot: React.FC<ChipMenuProps> = ({
  onClose,
  align = CHIP_MENU_ALIGN_LEFT,
  placement = CHIP_MENU_PLACEMENT_BOTTOM,
  width,
  activeTreatment = CHIP_MENU_ACTIVE_TREATMENT_PILL,
  className,
  portal = false,
  anchorRef,
  portalContainer,
  children,
  ariaLabel = 'Menu',
  dataTestId = 'chip-menu',
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<PortalCoords | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !isInside(ref.current, e)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Position is our responsibility in portal mode (the panel no longer
  // lives next to its anchor). Recompute from the trigger rect on open and
  // keep it current while scrolling/resizing — capture: true so we catch
  // scrolls on every intermediate overflow ancestor, not just the window.
  useLayoutEffect(() => {
    if (!portal) {
      setCoords(null);
      return undefined;
    }
    const update = () => {
      const rect = anchorRef?.current?.getBoundingClientRect();
      if (rect) setCoords(computePortalCoords(rect, align, placement));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [portal, anchorRef, align, placement]);

  const widthStyle = widthStyleOf(width);
  const positionStyle = portal && coords ? positionStyleOf(coords) : undefined;

  const style =
    widthStyle || positionStyle ? { ...widthStyle, ...positionStyle } : undefined;

  const panel = (
    <Panel
      ref={ref}
      role="menu"
      aria-label={ariaLabel}
      $align={align}
      $placement={placement}
      $portal={portal}
      style={style}
      className={className}
      data-testid={dataTestId}
      data-align={align}
      data-placement={placement}
      data-active-treatment={activeTreatment}
    >
      <ChipMenuActiveTreatmentContext.Provider value={activeTreatment}>
        {children}
      </ChipMenuActiveTreatmentContext.Provider>
    </Panel>
  );

  const portalTarget =
    portal && typeof document !== 'undefined' ? portalContainer ?? document.body : null;

  return portalTarget ? createPortal(panel, portalTarget) : panel;
};

const HeaderSlot: React.FC<ChipMenuHeaderProps> = ({
  children,
  cap,
  dataTestId = 'chip-menu__header',
}) => (
  <Header data-testid={dataTestId}>
    <span>{children}</span>
    {cap !== undefined && cap !== null && (
      <HeaderCap data-testid={`${dataTestId}__cap`}>{cap}</HeaderCap>
    )}
  </Header>
);

const SeparatorSlot: React.FC<{ dataTestId?: string }> = ({
  dataTestId = 'chip-menu__separator',
}) => <Separator data-testid={dataTestId} />;

const ListSlot: React.FC<{ children?: ReactNode; dataTestId?: string }> = ({
  children,
  dataTestId = 'chip-menu__list',
}) => <List data-testid={dataTestId}>{children}</List>;

const ItemSlot: React.FC<ChipMenuItemProps> = ({
  children,
  icon,
  meta,
  tone = CHIP_MENU_ITEM_TONE_DEFAULT,
  variant = CHIP_MENU_ITEM_VARIANT_ITEM,
  isActive = false,
  disabled = false,
  title,
  onClick,
  dataTestId = 'chip-menu__item',
}) => {
  const activeTreatment = useContext(ChipMenuActiveTreatmentContext);
  return (
    <StyledItem
      type="button"
      role="menuitem"
      $tone={tone}
      $variant={variant}
      $isActive={isActive}
      $activeTreatment={activeTreatment}
      disabled={disabled}
      title={title}
      onClick={onClick}
      data-testid={dataTestId}
      data-tone={tone}
      data-variant={variant}
      data-active={isActive || undefined}
    >
      {icon && (
        <span
          className="chip-menu-item__icon"
          aria-hidden="true"
          data-testid={`${dataTestId}__icon`}
        >
          {icon}
        </span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {meta !== undefined && meta !== null && (
        <ItemMeta data-testid={`${dataTestId}__meta`}>{meta}</ItemMeta>
      )}
    </StyledItem>
  );
};

type ChipMenuComponent = React.FC<ChipMenuProps> & {
  Header: typeof HeaderSlot;
  Separator: typeof SeparatorSlot;
  List: typeof ListSlot;
  Item: typeof ItemSlot;
};

const ChipMenu = ChipMenuRoot as ChipMenuComponent;
ChipMenu.Header = HeaderSlot;
ChipMenu.Separator = SeparatorSlot;
ChipMenu.List = ListSlot;
ChipMenu.Item = ItemSlot;

export default ChipMenu;
